import { toPng } from 'html-to-image';
import {
  Bold,
  Download,
  Image as ImageIcon,
  Move,
  Square,
  Trash2,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const ASPECT_RATIOS = [
  { label: '1:1', width: 1080, height: 1080 },
  { label: '4:3', width: 1080, height: 810 },
  { label: '4:5', width: 1080, height: 1350 },
  { label: '16:9', width: 1280, height: 720 },
  { label: '9:16', width: 720, height: 1280 },
] as const;

type RatioLabel = (typeof ASPECT_RATIOS)[number]['label'];

type LayerType = 'text' | 'card' | 'image';

interface LayerBase {
  id: string;
  type: LayerType;
  x: number;
  y: number;
  width: number;
  height: number;
}

interface TextLayer extends LayerBase {
  type: 'text';
  text: string;
  color: string;
  fontSize: number;
  fontFamily: string;
  fontWeight: 'normal' | 'bold';
}

interface CardLayer extends LayerBase {
  type: 'card';
  color: string;
}

interface ImageLayer extends LayerBase {
  type: 'image';
  src: string;
  aspectRatio: number;
}

type Layer = TextLayer | CardLayer | ImageLayer;

interface ActionState {
  mode: 'drag' | 'resize';
  layerId: string;
  startX: number;
  startY: number;
  originX: number;
  originY: number;
  originWidth: number;
  originHeight: number;
  originFontSize: number;
}

const FONT_FAMILIES = [
  'Noto Sans TC',
  'Arial',
  'Georgia',
  'Courier New',
  'Impact',
];

function createId() {
  return `layer_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

function clamp(n: number, min: number, max: number) {
  return Math.min(Math.max(n, min), max);
}

const TEXT_BOX_PAD_X = 16;
const TEXT_BOX_PAD_Y = 20;
const TEXT_BOX_MIN_W = 44;
const TEXT_BOX_MIN_H = 36;
const TEXT_BOX_SAFE_RIGHT = 8;
const TEXT_BOX_SAFE_BOTTOM = 8;

/**
 * 依目前字型與文字內容量測自然寬高（可換行，寬度上限為剩餘舞台寬度）。
 */
function measureNaturalTextSize(
  el: HTMLElement,
  stageEl: HTMLElement,
  maxContentWidth: number
): { w: number; h: number } {
  const cs = window.getComputedStyle(el);
  const clone = document.createElement('div');
  clone.innerText = el.innerText ?? '';
  clone.style.cssText = `
    position: absolute;
    left: -99999px;
    top: 0;
    width: max-content;
    max-width: ${maxContentWidth}px;
    white-space: pre-wrap;
    overflow-wrap: anywhere;
    word-break: break-word;
    font-size: ${cs.fontSize};
    font-family: ${cs.fontFamily};
    font-weight: ${cs.fontWeight};
    line-height: ${cs.lineHeight};
    letter-spacing: ${cs.letterSpacing};
    padding: 0;
    margin: 0;
    border: 0;
    box-sizing: border-box;
  `;
  stageEl.appendChild(clone);
  const w = clone.offsetWidth;
  const h = clone.offsetHeight;
  stageEl.removeChild(clone);
  return { w: Math.ceil(w), h: Math.ceil(h) };
}

/**
 * HTML 內容編輯器（DOM 版）：背景、拖放圖片、雙擊文字、白卡、一鍵輸出。
 *
 * @example
 * <HtmlContentEditorTool />
 */
export function HtmlContentEditorTool() {
  const stageHostRef = useRef<HTMLDivElement>(null);
  const stageViewportRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const textLayerRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const actionRef = useRef<ActionState | null>(null);
  const importHtmlInputRef = useRef<HTMLInputElement>(null);
  const [activeRatio, setActiveRatio] = useState<RatioLabel>('1:1');
  const [scale, setScale] = useState(1);
  const [layers, setLayers] = useState<Layer[]>([]);
  const [selectedLayerId, setSelectedLayerId] = useState<string | null>(null);
  const [hoveredLayerId, setHoveredLayerId] = useState<string | null>(null);
  const [isDropActive, setIsDropActive] = useState(false);
  const [backgroundImage, setBackgroundImage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const stageSize = useMemo(
    () => ASPECT_RATIOS.find(r => r.label === activeRatio) ?? ASPECT_RATIOS[0],
    [activeRatio]
  );

  const selectedLayer = useMemo(
    () => layers.find(layer => layer.id === selectedLayerId) ?? null,
    [layers, selectedLayerId]
  );

  const getStagePoint = useCallback(
    (clientX: number, clientY: number) => {
      const stage = stageRef.current;
      if (!stage) {
        return { x: 0, y: 0 };
      }
      const rect = stage.getBoundingClientRect();
      const scaleX = stageSize.width / rect.width;
      const scaleY = stageSize.height / rect.height;
      return {
        x: clamp((clientX - rect.left) * scaleX, 0, stageSize.width),
        y: clamp((clientY - rect.top) * scaleY, 0, stageSize.height),
      };
    },
    [stageSize.height, stageSize.width]
  );

  useEffect(() => {
    const host = stageHostRef.current;
    if (!host) {
      return;
    }
    const resize = () => {
      const hostWidth = host.clientWidth;
      const maxHeight = Math.floor(window.innerHeight * 0.72);
      const scaleByWidth = hostWidth / stageSize.width;
      const scaleByHeight = maxHeight / stageSize.height;
      setScale(Math.min(scaleByWidth, scaleByHeight, 1));
    };
    resize();
    const observer = new ResizeObserver(resize);
    observer.observe(host);
    window.addEventListener('resize', resize);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', resize);
    };
  }, [stageSize.height, stageSize.width]);

  const updateLayer = useCallback(
    (layerId: string, updater: (layer: Layer) => Layer) => {
      setLayers(prev =>
        prev.map(layer => (layer.id === layerId ? updater(layer) : layer))
      );
    },
    []
  );

  const fitTextBoxToContent = useCallback(
    (layerId: string) => {
      const stageEl = stageRef.current;
      const el = textLayerRefs.current[layerId];
      if (!stageEl || !el) {
        return;
      }
      setLayers(prev => {
        const layer = prev.find(l => l.id === layerId && l.type === 'text') as
          | TextLayer
          | undefined;
        if (!layer) {
          return prev;
        }
        const maxContentW = Math.max(40, stageSize.width - layer.x);
        const measured = measureNaturalTextSize(el, stageEl, maxContentW);
        let nextW = clamp(
          measured.w + TEXT_BOX_PAD_X,
          TEXT_BOX_MIN_W,
          stageSize.width - layer.x
        );
        let nextH = clamp(
          measured.h + TEXT_BOX_PAD_Y,
          TEXT_BOX_MIN_H,
          stageSize.height - layer.y
        );
        nextW = Math.min(
          nextW,
          stageSize.width - layer.x - TEXT_BOX_SAFE_RIGHT
        );
        nextH = Math.min(
          nextH,
          stageSize.height - layer.y - TEXT_BOX_SAFE_BOTTOM
        );
        nextW = Math.max(nextW, TEXT_BOX_MIN_W);
        nextH = Math.max(nextH, TEXT_BOX_MIN_H);
        let nextX = layer.x;
        if (nextX + nextW > stageSize.width) {
          nextX = clamp(stageSize.width - nextW, 0, stageSize.width - nextW);
        }
        if (
          Math.abs(nextW - layer.width) < 1 &&
          Math.abs(nextH - layer.height) < 1 &&
          Math.abs(nextX - layer.x) < 1
        ) {
          return prev;
        }
        return prev.map(l =>
          l.id === layerId && l.type === 'text'
            ? { ...(l as TextLayer), x: nextX, width: nextW, height: nextH }
            : l
        );
      });
    },
    [stageSize.height, stageSize.width]
  );

  const startDrag = useCallback(
    (e: React.PointerEvent<HTMLButtonElement>, layer: Layer) => {
      e.preventDefault();
      e.stopPropagation();
      const point = getStagePoint(e.clientX, e.clientY);
      actionRef.current = {
        mode: 'drag',
        layerId: layer.id,
        startX: point.x,
        startY: point.y,
        originX: layer.x,
        originY: layer.y,
        originWidth: layer.width,
        originHeight: layer.height,
        originFontSize: layer.type === 'text' ? layer.fontSize : 0,
      };
      setSelectedLayerId(layer.id);
    },
    [getStagePoint]
  );

  const startResize = useCallback(
    (e: React.PointerEvent<HTMLButtonElement>, layer: Layer) => {
      e.preventDefault();
      e.stopPropagation();
      const point = getStagePoint(e.clientX, e.clientY);
      actionRef.current = {
        mode: 'resize',
        layerId: layer.id,
        startX: point.x,
        startY: point.y,
        originX: layer.x,
        originY: layer.y,
        originWidth: layer.width,
        originHeight: layer.height,
        originFontSize: layer.type === 'text' ? layer.fontSize : 0,
      };
      setSelectedLayerId(layer.id);
    },
    [getStagePoint]
  );

  useEffect(() => {
    const onMove = (event: PointerEvent) => {
      const action = actionRef.current;
      if (!action) {
        return;
      }
      const point = getStagePoint(event.clientX, event.clientY);
      const deltaX = point.x - action.startX;
      const deltaY = point.y - action.startY;
      setLayers(prev =>
        prev.map(layer => {
          if (layer.id !== action.layerId) {
            return layer;
          }

          if (action.mode === 'drag') {
            const nextX = clamp(
              action.originX + deltaX,
              0,
              stageSize.width - layer.width
            );
            const nextY = clamp(
              action.originY + deltaY,
              0,
              stageSize.height - layer.height
            );
            return { ...layer, x: nextX, y: nextY };
          }

          if (layer.type === 'text') {
            const ratio = action.originWidth / Math.max(action.originHeight, 1);
            const scaleByX =
              (action.originWidth + deltaX) / Math.max(action.originWidth, 1);
            const scaleByY =
              (action.originHeight + deltaY) / Math.max(action.originHeight, 1);
            const rawScale = Math.max(scaleByX, scaleByY);
            const minScaleBySize = Math.max(
              TEXT_BOX_MIN_W / Math.max(action.originWidth, 1),
              TEXT_BOX_MIN_H / Math.max(action.originHeight, 1)
            );
            const maxScaleByBound = Math.min(
              (stageSize.width - layer.x) / Math.max(action.originWidth, 1),
              (stageSize.height - layer.y) / Math.max(action.originHeight, 1)
            );
            const nextScale = clamp(rawScale, minScaleBySize, maxScaleByBound);
            const nextWidth = action.originWidth * nextScale;
            const nextHeight = nextWidth / Math.max(ratio, 0.0001);
            const nextFontSize = clamp(
              action.originFontSize * nextScale,
              14,
              180
            );
            return {
              ...layer,
              fontSize: nextFontSize,
              width: nextWidth,
              height: nextHeight,
            };
          }

          if (layer.type === 'image') {
            const nextWidth = clamp(
              action.originWidth + deltaX,
              80,
              stageSize.width - layer.x
            );
            const nextHeight = clamp(
              nextWidth / layer.aspectRatio,
              80,
              stageSize.height - layer.y
            );
            return {
              ...layer,
              width: nextWidth,
              height: nextHeight,
            };
          }

          const ratio = action.originWidth / Math.max(action.originHeight, 1);
          const scaleByX =
            (action.originWidth + deltaX) / Math.max(action.originWidth, 1);
          const scaleByY =
            (action.originHeight + deltaY) / Math.max(action.originHeight, 1);
          const rawScale = Math.max(scaleByX, scaleByY);
          const minScaleBySize = Math.max(
            80 / Math.max(action.originWidth, 1),
            80 / Math.max(action.originHeight, 1)
          );
          const maxScaleByBound = Math.min(
            (stageSize.width - layer.x) / Math.max(action.originWidth, 1),
            (stageSize.height - layer.y) / Math.max(action.originHeight, 1)
          );
          const nextScale = clamp(rawScale, minScaleBySize, maxScaleByBound);
          const nextWidth = action.originWidth * nextScale;
          const nextHeight = nextWidth / Math.max(ratio, 0.0001);
          return { ...layer, width: nextWidth, height: nextHeight };
        })
      );
    };

    const onUp = () => {
      actionRef.current = null;
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
  }, [fitTextBoxToContent, getStagePoint, stageSize.height, stageSize.width]);

  const handleBackgroundUpload = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) {
        return;
      }
      setError(null);
      const reader = new FileReader();
      reader.onload = () => {
        if (typeof reader.result !== 'string') {
          setError('背景圖片讀取失敗，請重試。');
          return;
        }
        setBackgroundImage(reader.result);
      };
      reader.onerror = () => {
        setError('背景圖片讀取失敗，請重試。');
      };
      reader.readAsDataURL(file);
      e.target.value = '';
    },
    []
  );

  const addCard = useCallback(() => {
    const layer: CardLayer = {
      id: createId(),
      type: 'card',
      x: Math.max(24, stageSize.width * 0.2),
      y: Math.max(24, stageSize.height * 0.25),
      width: 360,
      height: 240,
      color: '#ffffff',
    };
    setLayers(prev => [...prev, layer]);
    setSelectedLayerId(layer.id);
  }, [stageSize.height, stageSize.width]);

  const addTextAtPoint = useCallback(
    (x: number, y: number) => {
      const layer: TextLayer = {
        id: createId(),
        type: 'text',
        x: clamp(x, 0, stageSize.width - 260),
        y: clamp(y, 0, stageSize.height - 120),
        width: 260,
        height: 120,
        text: '輸入文字',
        color: '#1c1410',
        fontSize: 42,
        fontFamily: 'Noto Sans TC',
        fontWeight: 'bold',
      };
      setLayers(prev => [...prev, layer]);
      setSelectedLayerId(layer.id);
      requestAnimationFrame(() => {
        fitTextBoxToContent(layer.id);
      });
    },
    [fitTextBoxToContent, stageSize.height, stageSize.width]
  );

  const handleStageDoubleClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (e.target !== e.currentTarget) {
        return;
      }
      const point = getStagePoint(e.clientX, e.clientY);
      addTextAtPoint(point.x, point.y);
    },
    [addTextAtPoint, getStagePoint]
  );

  const loadImageLayer = useCallback(
    (file: File, dropX: number, dropY: number) => {
      const reader = new FileReader();
      reader.onload = () => {
        if (typeof reader.result !== 'string') {
          setError('圖片讀取失敗，請重試。');
          return;
        }
        const img = new Image();
        img.onload = () => {
          const aspectRatio = img.width / Math.max(img.height, 1);
          const baseWidth = clamp(
            stageSize.width * 0.35,
            160,
            stageSize.width * 0.8
          );
          const width = baseWidth;
          const height = clamp(
            width / aspectRatio,
            120,
            stageSize.height * 0.8
          );
          const layer: ImageLayer = {
            id: createId(),
            type: 'image',
            src: reader.result as string,
            x: clamp(dropX - width / 2, 0, stageSize.width - width),
            y: clamp(dropY - height / 2, 0, stageSize.height - height),
            width,
            height,
            aspectRatio,
          };
          setLayers(prev => [...prev, layer]);
          setSelectedLayerId(layer.id);
        };
        img.onerror = () => setError('圖片載入失敗，請換一張圖再試。');
        img.src = reader.result;
      };
      reader.onerror = () => setError('圖片讀取失敗，請重試。');
      reader.readAsDataURL(file);
    },
    [stageSize.height, stageSize.width]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setIsDropActive(false);
      setError(null);
      const file = Array.from(e.dataTransfer.files).find(item =>
        item.type.startsWith('image/')
      );
      if (!file) {
        return;
      }
      const point = getStagePoint(e.clientX, e.clientY);
      loadImageLayer(file, point.x, point.y);
    },
    [getStagePoint, loadImageLayer]
  );

  const handleDeleteSelected = useCallback(() => {
    if (!selectedLayerId) {
      return;
    }
    setLayers(prev => prev.filter(layer => layer.id !== selectedLayerId));
    setSelectedLayerId(null);
  }, [selectedLayerId]);

  const updateSelectedColor = useCallback(
    (color: string) => {
      if (!selectedLayer) {
        return;
      }
      if (selectedLayer.type === 'text') {
        updateLayer(selectedLayer.id, layer => ({
          ...(layer as TextLayer),
          color,
        }));
      }
      if (selectedLayer.type === 'card') {
        updateLayer(selectedLayer.id, layer => ({
          ...(layer as CardLayer),
          color,
        }));
      }
    },
    [selectedLayer, updateLayer]
  );

  const handleDownload = useCallback(async () => {
    if (!stageViewportRef.current) {
      return;
    }
    try {
      // 確保字型與版面完成，避免匯出時重排造成所見與輸出不一致。
      if (typeof document !== 'undefined' && 'fonts' in document) {
        await (document as Document & { fonts: FontFaceSet }).fonts.ready;
      }
      await new Promise<void>(resolve => {
        requestAnimationFrame(() => resolve());
      });
      const dataUrl = await toPng(stageViewportRef.current, {
        cacheBust: true,
        pixelRatio: 2,
      });
      const anchor = document.createElement('a');
      anchor.href = dataUrl;
      anchor.download = 'content.png';
      anchor.click();
    } catch {
      setError('截圖失敗，請重試。');
    }
  }, []);

  const handleDownloadHtml = useCallback(() => {
    const payload = {
      version: 1,
      ratio: activeRatio,
      backgroundImage,
      layers,
      exportedAt: new Date().toISOString(),
    };
    const stageMarkup = stageRef.current?.innerHTML ?? '';
    const html = `<!doctype html>
<html lang="zh-TW">
  <head>
    <meta charset="UTF-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1.0" />
    <title>HTML Content Editor Export</title>
    <style>
      body { margin: 0; padding: 24px; background: #111; color: #eee; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; }
      .note { margin-bottom: 12px; font-size: 13px; color: #bbb; }
      .stage {
        width: ${stageSize.width}px;
        height: ${stageSize.height}px;
        position: relative;
        overflow: hidden;
        background-color: #e5e7eb;
        background-image: ${backgroundImage ? `url(${JSON.stringify(backgroundImage)})` : 'none'};
        background-size: cover;
        background-position: center;
      }
    </style>
  </head>
  <body>
    <div class="note">此檔案可回到工具頁用「匯入 HTML」重新編輯。</div>
    <div class="stage">${stageMarkup}</div>
    <script id="emvyaxpos-editor-state" type="application/json">${JSON.stringify(payload).replace(/</g, '\\u003c')}</script>
  </body>
</html>`;
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = 'content-editor.html';
    anchor.click();
    URL.revokeObjectURL(url);
  }, [activeRatio, backgroundImage, layers, stageSize.height, stageSize.width]);

  const handleImportHtml = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) {
        return;
      }
      setError(null);
      const reader = new FileReader();
      reader.onload = () => {
        try {
          const text = typeof reader.result === 'string' ? reader.result : '';
          const parser = new DOMParser();
          const doc = parser.parseFromString(text, 'text/html');
          const stateNode = doc.querySelector('#emvyaxpos-editor-state');
          if (!stateNode?.textContent) {
            throw new Error('missing-state');
          }
          const parsed = JSON.parse(stateNode.textContent) as {
            version: number;
            ratio: RatioLabel;
            backgroundImage: string | null;
            layers: Layer[];
          };
          if (!ASPECT_RATIOS.some(item => item.label === parsed.ratio)) {
            throw new Error('invalid-ratio');
          }
          setActiveRatio(parsed.ratio);
          setBackgroundImage(parsed.backgroundImage ?? null);
          setLayers(Array.isArray(parsed.layers) ? parsed.layers : []);
          setSelectedLayerId(null);
        } catch {
          setError('匯入失敗：請選擇由本工具匯出的 HTML。');
        }
      };
      reader.onerror = () => {
        setError('匯入失敗：檔案讀取錯誤。');
      };
      reader.readAsText(file, 'utf-8');
      e.target.value = '';
    },
    []
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2 rounded-lg border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800">
        {ASPECT_RATIOS.map(ratio => (
          <button
            key={ratio.label}
            type="button"
            onClick={() => setActiveRatio(ratio.label)}
            className={`rounded px-2.5 py-1 text-xs font-medium transition-colors ${
              activeRatio === ratio.label
                ? 'bg-primary text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
            }`}
          >
            {ratio.label}
          </button>
        ))}

        <div className="h-5 w-px bg-gray-300 dark:bg-gray-600" />

        <label className="flex cursor-pointer items-center gap-1.5 rounded bg-gray-100 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600">
          <ImageIcon className="h-3.5 w-3.5" />
          設定背景
          <input
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleBackgroundUpload}
          />
        </label>

        <button
          type="button"
          onClick={addCard}
          className="flex items-center gap-1.5 rounded bg-gray-100 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
        >
          <Square className="h-3.5 w-3.5" />
          加白卡
        </button>

        <button
          type="button"
          onClick={handleDeleteSelected}
          disabled={!selectedLayer}
          className="flex items-center gap-1 rounded bg-red-100 px-2.5 py-1 text-xs font-medium text-red-700 hover:bg-red-200 disabled:opacity-50 dark:bg-red-900/30 dark:text-red-400"
        >
          <Trash2 className="h-3.5 w-3.5" />
          刪除
        </button>

        <div className="ml-auto">
          <button
            type="button"
            onClick={() => importHtmlInputRef.current?.click()}
            className="mr-2 rounded bg-gray-100 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
          >
            匯入 HTML
          </button>
          <button
            type="button"
            onClick={handleDownloadHtml}
            className="mr-2 rounded bg-gray-100 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
          >
            下載 HTML
          </button>
          <button
            type="button"
            onClick={handleDownload}
            className="flex items-center gap-1.5 rounded bg-primary px-3 py-1.5 text-xs font-medium text-white hover:bg-primary/90"
          >
            <Download className="h-3.5 w-3.5" />
            下載 PNG
          </button>
          <input
            ref={importHtmlInputRef}
            type="file"
            accept=".html,text/html"
            className="hidden"
            onChange={handleImportHtml}
          />
        </div>
      </div>

      <div ref={stageHostRef} className="w-full">
        <div
          ref={stageViewportRef}
          className={`relative mx-auto overflow-hidden rounded-lg border-2 transition-colors ${
            isDropActive
              ? 'border-dashed border-primary bg-primary/5'
              : 'border-gray-200 dark:border-gray-700'
          }`}
          style={{
            width: stageSize.width * scale,
            height: stageSize.height * scale,
          }}
          onDragOver={e => {
            e.preventDefault();
            setIsDropActive(true);
          }}
          onDragLeave={() => setIsDropActive(false)}
          onDrop={handleDrop}
        >
          <div
            ref={stageRef}
            data-testid="html-content-stage"
            onClick={e => {
              if (e.target === e.currentTarget) {
                setSelectedLayerId(null);
              }
            }}
            onDoubleClick={handleStageDoubleClick}
            className="relative select-none"
            style={{
              width: stageSize.width,
              height: stageSize.height,
              transform: `scale(${scale})`,
              transformOrigin: 'top left',
              backgroundColor: '#e5e7eb',
              backgroundImage: backgroundImage
                ? `url(${backgroundImage})`
                : 'none',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          >
            {layers.map(layer => {
              const selected = selectedLayerId === layer.id;
              const controlsVisible = selected || hoveredLayerId === layer.id;
              return (
                <div
                  key={layer.id}
                  className="absolute"
                  style={{
                    left: layer.x,
                    top: layer.y,
                    width: layer.width,
                    height: layer.height,
                  }}
                  onMouseEnter={() => setHoveredLayerId(layer.id)}
                  onMouseLeave={() =>
                    setHoveredLayerId(prev => (prev === layer.id ? null : prev))
                  }
                  onMouseDown={() => {
                    setSelectedLayerId(layer.id);
                    if (layer.type === 'text') {
                      requestAnimationFrame(() => {
                        fitTextBoxToContent(layer.id);
                      });
                    }
                  }}
                >
                  {layer.type === 'text' && (
                    <div
                      contentEditable
                      suppressContentEditableWarning
                      ref={el => {
                        textLayerRefs.current[layer.id] = el;
                        if (!el) {
                          return;
                        }
                        // 避免輸入中被 React 重繪覆蓋，導致 caret 跳回開頭。
                        if (document.activeElement === el) {
                          return;
                        }
                        if (el.innerText !== layer.text) {
                          el.innerText = layer.text;
                        }
                      }}
                      onInput={e => {
                        const value = e.currentTarget.innerText ?? '';
                        updateLayer(layer.id, current => ({
                          ...(current as TextLayer),
                          text: value,
                        }));
                        requestAnimationFrame(() => {
                          fitTextBoxToContent(layer.id);
                        });
                      }}
                      className="h-full w-full overflow-hidden whitespace-pre-wrap break-words bg-transparent outline-none"
                      style={{
                        color: layer.color,
                        fontSize: layer.fontSize,
                        fontFamily: layer.fontFamily,
                        fontWeight: layer.fontWeight,
                        lineHeight: 1.35,
                      }}
                    />
                  )}

                  {layer.type === 'card' && (
                    <div
                      className="h-full w-full rounded-2xl border border-gray-200 shadow-sm"
                      style={{ backgroundColor: layer.color }}
                    />
                  )}

                  {layer.type === 'image' && (
                    <img
                      src={layer.src}
                      alt="拖入圖片"
                      className="h-full w-full rounded-xl object-cover"
                      draggable={false}
                    />
                  )}

                  {controlsVisible && (
                    <>
                      <button
                        type="button"
                        aria-label="拖曳圖層"
                        data-export-ignore="true"
                        className={`absolute -left-3 -top-3 flex h-6 w-6 items-center justify-center rounded-full bg-cyan-700 text-white transition-opacity ${
                          controlsVisible
                            ? 'opacity-100'
                            : 'pointer-events-none opacity-0'
                        }`}
                        onPointerDown={e => startDrag(e, layer)}
                      >
                        <Move className="h-3.5 w-3.5" />
                      </button>

                      <button
                        type="button"
                        aria-label="刪除圖層"
                        data-export-ignore="true"
                        className={`absolute -right-3 -top-3 flex h-6 w-6 items-center justify-center rounded-full bg-gray-900 text-white transition-opacity ${
                          controlsVisible
                            ? 'opacity-100'
                            : 'pointer-events-none opacity-0'
                        }`}
                        onClick={() => {
                          setLayers(prev =>
                            prev.filter(item => item.id !== layer.id)
                          );
                          setSelectedLayerId(null);
                        }}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>

                      <button
                        type="button"
                        aria-label="縮放圖層"
                        data-export-ignore="true"
                        className={`absolute -bottom-4 -right-4 flex h-8 w-8 items-center justify-center rounded-full bg-transparent transition-opacity ${
                          controlsVisible
                            ? 'opacity-100'
                            : 'pointer-events-none opacity-0'
                        }`}
                        onPointerDown={e => startResize(e, layer)}
                      >
                        <span className="h-4 w-4 rounded-full bg-orange-500 shadow" />
                      </button>
                    </>
                  )}
                </div>
              );
            })}

            {isDropActive && (
              <div
                data-export-ignore="true"
                className="pointer-events-none absolute inset-0 flex items-center justify-center"
              >
                <div className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white shadow-lg">
                  放開以加入圖片圖層
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {selectedLayer && (
        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-blue-200 bg-blue-50 p-3 dark:border-blue-800 dark:bg-blue-900/20">
          <span className="text-xs font-semibold text-blue-800 dark:text-blue-200">
            {selectedLayer.type === 'text'
              ? '文字屬性'
              : selectedLayer.type === 'card'
                ? '白卡屬性'
                : '圖片屬性'}
          </span>

          {(selectedLayer.type === 'text' || selectedLayer.type === 'card') && (
            <label className="flex items-center gap-1.5 text-xs text-blue-700 dark:text-blue-300">
              顏色
              <input
                type="color"
                value={
                  selectedLayer.type === 'text'
                    ? selectedLayer.color
                    : selectedLayer.color
                }
                onChange={e => {
                  updateSelectedColor(e.target.value);
                  if (selectedLayer.type === 'text') {
                    requestAnimationFrame(() => {
                      fitTextBoxToContent(selectedLayer.id);
                    });
                  }
                }}
                className="h-7 w-10 cursor-pointer rounded border-0 p-0"
              />
            </label>
          )}

          {selectedLayer.type === 'text' && (
            <>
              <label className="flex items-center gap-1.5 text-xs text-blue-700 dark:text-blue-300">
                大小
                <input
                  type="number"
                  min={14}
                  max={180}
                  value={Math.round(selectedLayer.fontSize)}
                  onChange={e => {
                    const next = clamp(Number(e.target.value), 14, 180);
                    updateLayer(selectedLayer.id, layer => ({
                      ...(layer as TextLayer),
                      fontSize: next,
                    }));
                    requestAnimationFrame(() => {
                      fitTextBoxToContent(selectedLayer.id);
                    });
                  }}
                  className="w-16 rounded border border-blue-200 bg-white px-1.5 py-0.5 text-center text-xs text-gray-900 dark:border-blue-700 dark:bg-gray-800 dark:text-gray-100"
                />
              </label>

              <select
                value={selectedLayer.fontFamily}
                onChange={e => {
                  const family = e.target.value;
                  updateLayer(selectedLayer.id, layer => ({
                    ...(layer as TextLayer),
                    fontFamily: family,
                  }));
                  requestAnimationFrame(() => {
                    fitTextBoxToContent(selectedLayer.id);
                  });
                }}
                className="rounded border border-blue-200 bg-white px-1.5 py-0.5 text-xs text-gray-900 dark:border-blue-700 dark:bg-gray-800 dark:text-gray-100"
              >
                {FONT_FAMILIES.map(font => (
                  <option key={font} value={font}>
                    {font}
                  </option>
                ))}
              </select>

              <button
                type="button"
                onClick={() => {
                  const next =
                    selectedLayer.fontWeight === 'bold' ? 'normal' : 'bold';
                  updateLayer(selectedLayer.id, layer => ({
                    ...(layer as TextLayer),
                    fontWeight: next,
                  }));
                  requestAnimationFrame(() => {
                    fitTextBoxToContent(selectedLayer.id);
                  });
                }}
                className={`flex items-center gap-1 rounded border px-2 py-0.5 text-xs font-bold transition-colors ${
                  selectedLayer.fontWeight === 'bold'
                    ? 'border-blue-600 bg-blue-600 text-white'
                    : 'border-blue-300 bg-white text-blue-700 dark:border-blue-600 dark:bg-gray-800 dark:text-blue-300'
                }`}
              >
                <Bold className="h-3 w-3" />B
              </button>
            </>
          )}
        </div>
      )}

      <p className="text-xs text-gray-400 dark:text-gray-500">
        互動方式：雙擊空白處新增文字｜拖圖片進版面加入圖層｜點選圖層後可移動、縮放、刪除、改色
      </p>
      {error && (
        <p className="text-xs text-red-600 dark:text-red-400">{error}</p>
      )}
    </div>
  );
}
